import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View
} from '@react-pdf/renderer';
import { PtgFeaturesContextType } from './PtgFeaturesContextType';
import { EFlyerPDF } from '../EFlyerPDF';
import React from 'react';
import { useEFlyerQRCode } from '../QRCallToAction';
import { EFlyerFooterPDF, useHydrateFooter } from '../EFlyerFooter';

/** @typedef {import('./PtgFeaturesContextType').PtgFeaturesContext} PtgFeaturesContext */

export const PtgFeaturesPdf = ({ context }) => {
  const { qr } = useEFlyerQRCode(context.qrUrl);
  const footerData = useHydrateFooter(context.footer);

  if (!qr || !footerData.ready) {
    return null;
  }

  // disable hyphenation
  Font.registerHyphenationCallback(word => [word]);

  return (
    <EFlyerPDF>
      <PTGFeatures context={context} qr={qr} footerData={footerData} />
    </EFlyerPDF>
  );
};

PtgFeaturesPdf.propTypes = {
  context: PtgFeaturesContextType.isRequired
};

/**
 * Renders the PDF document for the Features template
 * @param {{context: PtgFeaturesContext}} props
 * @param {string} qr The data url of the generated QR code
 * @returns {React.JSX.Element}
 */
const PTGFeatures = ({ context, qr, footerData }) => {
  const { themeColors } = context;
  const styles = StyleSheet.create({
    page: {
      padding: 10
    },
    headerContainer: {
      backgroundColor: '#FFFFFF',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: 12,
      paddingBottom: 12,
      height: '12%',
      borderBottom: '5px solid ' + themeColors.color500
    },
    logo: {
      maxWidth: '80%',
      marginLeft: 24,
      objectFit: 'contain',
      paddingVertical: 10
    },
    headings: { paddingLeft: 36 },
    heading1: {
      fontSize: 18,
      lineHeight: '24pt',
      color: themeColors.color500
    },
    heading2: {
      fontSize: 18,
      color: themeColors.color500,
      fontWeight: 'bold'
    },
    mainContainer: {
      paddingLeft: 50,
      paddingRight: 50,
      height: '61%'
    },
    title: {
      color: themeColors.color600,
      fontSize: 26,
      fontWeight: 'bold',
      marginTop: 20,
      marginBottom: 20
    },
    contentRow: {
      display: 'flex',
      flexDirection: 'row'
    },
    imageContainer: {
      width: '50%',
      paddingLeft: 10,
      paddingRight: 40
    },
    mainImage: {
      maxHeight: 200,
      objectFit: 'contain',
      objectPosition: 'left'
    },
    paragraph: {
      paddingTop: 22,
      fontSize: 18,
      lineHeight: '20pt',
      width: '50%'
    },
    featuresHeading: {
      color: '#000000',
      fontSize: 15,
      fontWeight: 'bold',
      marginTop: 30,
      marginBottom: 5
    },
    featuresList: {
      fontSize: 15,
      lineHeight: '18pt'
    },
    featureListItem: {
      display: 'flex',
      flexDirection: 'row'
    },
    feature: {},
    bullet: {
      fontSize: 14,
      color: '#000000',
      paddingRight: 20
    },
    callToActionContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      height: '15%',
      paddingLeft: 30,
      paddingRight: 30
    },
    qrCode: {
      height: 60,
      width: 60,
      marginRight: 10
    },
    callToAction: {
      fontWeight: 'bold',
      fontSize: 12
    },
    url: {
      fontSize: 12
    },
    footerContainer: {
      backgroundColor: themeColors.color500,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: '12%'
    },
    wHalf: { width: '50%' }
  });

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.headerContainer}>
          <View style={styles.wHalf}>
            <Image src={context.logoUrl} style={styles.logo} />
          </View>
          <View style={[styles.wHalf, styles.headings]}>
            <Text style={styles.heading1}>{context.heading1}</Text>
            <Text style={styles.heading2}>{context.heading2}</Text>
          </View>
        </View>
        <View style={styles.mainContainer}>
          <Text style={styles.title}>{context.title}</Text>
          <View style={styles.contentRow}>
            <View style={styles.imageContainer}>
              <Image src={context.mainImageUrl} style={styles.mainImage} />
            </View>
            <Text style={styles.paragraph}>{context.paragraph}</Text>
          </View>
          <View>
            <Text style={styles.featuresHeading}>
              {context.featuresHeading}
            </Text>
            <View style={styles.featuresList}>
              {Array.isArray(context.features) && context.features.length > 0
                ? context.features.map((feature, idx) => (
                    <View style={styles.featureListItem} key={idx}>
                      <Text style={styles.bullet}>•</Text>
                      <Text style={styles.feature}>{feature}</Text>
                    </View>
                  ))
                : null}
            </View>
          </View>
        </View>
        <View style={styles.callToActionContainer}>
          <View>
            <Image src={qr} style={styles.qrCode} />
          </View>
          <View>
            <Text style={styles.callToAction}>{context.callToAction}</Text>
            <Text style={styles.url}>{context.qrUrl}</Text>
          </View>
        </View>
        <View style={styles.footerContainer}>
          <EFlyerFooterPDF footerData={footerData} />
        </View>
      </Page>
    </Document>
  );
};
